import React, { useRef, useState, useEffect } from 'react';
import { IoAddCircle, IoClose, IoSearch, IoRefreshOutline, IoLanguage, IoChevronDown, IoChevronUp } from "react-icons/io5";
import { AiOutlineLoading } from "react-icons/ai";
import RecommendedPDF from './RecommendedPDF';
import { useTranslation } from 'react-i18next';
import { languageOptions } from '../utils/translation';
import { motion, AnimatePresence } from 'framer-motion';
import HorizontalShelf from './HorizontalShelf';

const KeywordSubscriptions = ({
  isDarkMode,
  keywords,
  showSubscriptionInput,
  toggleSubscriptionInput,
  newKeyword,
  setNewKeyword,
  handleAddKeyword,
  handleRemoveKeyword,
  handleKeywordClick,
  selectedKeyword,
  displayedKeywords,
  isLoading,
  displayedPdfs,
  handlePubMedClick,
  showAllPapers,
  toggleShowAllPapers,
  loadingKeywords,
  handleUpdateAll
}) => {
  const { t, i18n } = useTranslation();
  const subscriptionInputRef = useRef(null);
  const langMenuRef = useRef(null);

  const [loadingCaption, setLoadingCaption] = useState('');
  const [isUpdateAllClicked, setIsUpdateAllClicked] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showLangDropdown, setShowLangDropdown] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState(
    localStorage.getItem('selectedLanguage') || 'zh'
  );
  const [showLanguageNotification, setShowLanguageNotification] = useState(false);
  const [selectedLangName, setSelectedLangName] = useState('');

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (subscriptionInputRef.current && !subscriptionInputRef.current.contains(event.target)) {
        toggleSubscriptionInput(false);
      }
      
      if (langMenuRef.current && !langMenuRef.current.contains(event.target)) {
        setShowLangDropdown(false);
      }
    };

    if (showSubscriptionInput || showLangDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSubscriptionInput, toggleSubscriptionInput, showLangDropdown]);

  useEffect(() => {
    if (isLoading) {
      setLoadingCaption(t('homeTab.searchingPubmed'));
    }
  }, [isLoading, t]);

  useEffect(() => {
    if (showSubscriptionInput && subscriptionInputRef.current) {
      subscriptionInputRef.current.querySelector('input').focus();
    }
  }, [showSubscriptionInput]);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleAddKeyword();
    }
  };

  const handleUpdateAllClick = async () => {
    setIsUpdateAllClicked(true);
    setIsProcessing(true);
    await handleUpdateAll();
    setIsUpdateAllClicked(false);
    setIsProcessing(false);
  };

  const handleLanguageChange = (lang) => {
    setSelectedLanguage(lang);
    localStorage.setItem('selectedLanguage', lang);
    setShowLangDropdown(false);
    
    // Get the language name for the notification
    const langOption = languageOptions.find(option => option.value === lang);
    setSelectedLangName(langOption ? langOption.label : 'Selected Language');
    
    // Show notification
    setShowLanguageNotification(true);
    setTimeout(() => setShowLanguageNotification(false), 2000);
    
    // Map Baidu language codes to i18n language codes
    const langMap = {
      'zh': 'zh',      // Chinese Simplified
      'cht': 'zh-TW',  // Chinese Traditional
      'spa': 'es',     // Spanish
      'fra': 'fr',     // French
      'de': 'de',      // German
      'jp': 'ja',      // Japanese
      'ru': 'ru',      // Russian
    };
    
    // Only change i18n language if supported
    if (langMap[lang] && ['zh', 'en'].includes(langMap[lang])) {
      i18n.changeLanguage(langMap[lang]);
    }
  };

  const getSelectedLanguageLabel = () => {
    const lang = languageOptions.find(lang => lang.value === selectedLanguage);
    return lang ? lang.label.split(' ')[0] : 'Language';
  };

  return (
    <section className={`relative shrink-0 overflow-hidden rounded-[28px] border p-4 md:p-5 ${
      isDarkMode
        ? 'border-white/8 bg-[#09111f]/82 shadow-[0_24px_70px_rgba(2,6,23,0.45)]'
        : 'border-slate-200/90 bg-white/88 shadow-[0_20px_70px_rgba(148,163,184,0.18)]'
    }`}>
      <AnimatePresence>
        {showLanguageNotification && (
          <motion.div 
            className={`absolute right-4 top-4 z-50 flex items-center rounded-full px-3 py-1.5 text-xs shadow-lg ${
              isDarkMode ? 'bg-blue-900/80 text-blue-200' : 'bg-blue-100 text-blue-700'
            } backdrop-blur-sm`}
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <IoLanguage className="mr-1.5" size={14} />
            {t('common.languageChanged')}: <span className="ml-1 font-semibold">{selectedLangName}</span>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <div className={`inline-flex items-center rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] ${
            isDarkMode ? 'bg-white/6 text-slate-300' : 'bg-slate-100 text-slate-600'
          }`}>
            {t('homeTab.pubmedRadar', 'PubMed radar')}
          </div>
          <h2 className={`mt-3 text-2xl font-semibold tracking-tight ${
            isDarkMode ? 'text-white' : 'text-slate-900'
          }`}>
            {t('homeTab.keywords')}
          </h2>
          <p className={`mt-2 text-sm ${
            isDarkMode ? 'text-slate-300' : 'text-slate-600'
          }`}>
            {t('homeTab.latestPapers')} ({new Date().toLocaleDateString(i18n.language === 'zh' ? 'zh-CN' : 'en-US', { year: 'numeric', month: '2-digit', day: '2-digit' })})
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={toggleSubscriptionInput}
            className={`inline-flex items-center gap-2 rounded-full px-3 py-2 text-sm font-semibold ${
              isDarkMode ? 'bg-white/6 text-slate-200 hover:bg-white/10' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            <IoAddCircle size={18} />
            {t('homeTab.enterKeyword', 'Add keyword')}
          </button>

          <button
            onClick={handleUpdateAllClick}
            className={`inline-flex items-center gap-2 rounded-full px-3 py-2 text-sm font-semibold ${
              isDarkMode 
                ? 'bg-white/6 text-slate-200 hover:bg-white/10' 
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
            title={t('homeTab.updateAllKeywords')}
          >
            <IoRefreshOutline size={16} className={isProcessing ? 'animate-spin' : ''} />
            {isUpdateAllClicked ? t('common.loading') : t('homeTab.updateAllKeywords')}
          </button>

          <div className="relative" ref={langMenuRef}>
            <motion.button
              onClick={() => setShowLangDropdown(!showLangDropdown)}
              className={`flex items-center space-x-1 rounded-full border px-3 py-2 text-sm font-semibold transition-all duration-200 ${
                isDarkMode 
                  ? 'border-blue-800/30 bg-blue-900/20 text-blue-300 hover:bg-blue-900/30' 
                  : 'border-blue-200 bg-blue-100/80 text-blue-700 hover:bg-blue-200/80'
              } backdrop-blur-sm`}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
            >
              <IoLanguage className={`mr-1 ${showLangDropdown ? 'animate-pulse' : ''}`} size={16} />
              <span>{getSelectedLanguageLabel()}</span>
              <motion.span
                animate={{ rotate: showLangDropdown ? 180 : 0 }}
                transition={{ duration: 0.3 }}
              >
                <IoChevronDown className="ml-1" size={14} />
              </motion.span>
            </motion.button>

            {showLangDropdown && (
              <motion.div 
                className={`absolute right-0 mt-2 z-50 max-h-52 w-48 overflow-y-auto rounded-2xl py-1 shadow-lg ${
                  isDarkMode 
                    ? 'border border-[#30363d] bg-[#21262d]' 
                    : 'border border-gray-200 bg-white'
                }`}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
              >
                {languageOptions.map((lang) => (
                  <motion.button
                    key={lang.value}
                    onClick={() => handleLanguageChange(lang.value)}
                    className={`w-full px-3 py-1.5 text-left text-xs ${
                      selectedLanguage === lang.value 
                        ? (isDarkMode 
                            ? 'bg-blue-900/30 text-blue-300' 
                            : 'bg-blue-100 text-blue-700'
                          )
                        : (isDarkMode 
                            ? 'text-gray-300 hover:bg-[#30363d]' 
                            : 'text-gray-700 hover:bg-gray-100'
                          )
                    } transition-colors`}
                    whileHover={{ x: 2 }}
                  >
                    {lang.label}
                  </motion.button>
                ))}
              </motion.div>
            )}
          </div>

          <button
            onClick={toggleShowAllPapers}
            className={`inline-flex items-center gap-2 rounded-full px-3 py-2 text-sm font-semibold ${
              isDarkMode ? 'bg-white/6 text-slate-200 hover:bg-white/10' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            {showAllPapers ? t('homeTab.hideAll') : t('homeTab.showAll')}
            {showAllPapers ? <IoChevronUp size={16} /> : <IoChevronDown size={16} />}
          </button>
        </div>
      </div>

      {showSubscriptionInput && (
        <div className="mt-5" ref={subscriptionInputRef}>
          <div className={`flex items-center overflow-hidden rounded-full shadow-md md:w-[420px] ${
            isDarkMode ? 'bg-[#21262d]' : 'bg-white'
          }`}>
            <input
              type="text"
              value={newKeyword}
              onChange={(e) => setNewKeyword(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={t('homeTab.enterKeyword')}
              className={`flex-grow p-2 pl-4 outline-none ${isDarkMode ? 'bg-[#21262d] text-gray-200' : 'bg-white text-gray-800'}`}
            />
            <button
              onClick={handleAddKeyword}
              className={`p-2 ${isDarkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-500'}`}
            >
              <IoSearch size={20} />
            </button>
          </div>
        </div>
      )}

      <div className="mt-4 flex flex-wrap gap-2">
        {keywords.map((k) => (
          <div
            key={k.id}
            className={`flex items-center rounded-full px-3 py-1.5 text-xs cursor-pointer ${
              k.keyword === selectedKeyword || displayedKeywords.has(k.keyword)
                ? 'bg-blue-500 text-white'
                : isDarkMode
                ? 'bg-white/8 text-gray-200'
                : 'bg-slate-100 text-slate-700'
            }`}
          >
            <span className="mr-2" onClick={() => handleKeywordClick(k.keyword)}>
              {k.keyword}
              {loadingKeywords[k.keyword] && (
                <AiOutlineLoading className="ml-1 inline-block animate-spin" />
              )}
            </span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleRemoveKeyword(k.id, k.keyword);
              }}
              className={`rounded-full p-1 ${isDarkMode ? 'hover:bg-yellow-700' : 'hover:bg-yellow-200'}`}
            >
              <IoClose size={12} />
            </button>
          </div>
        ))}
      </div>

      {isLoading && (
        <div className="my-8 flex flex-col items-center justify-center">
          <AiOutlineLoading className={`animate-spin text-4xl ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} />
          <p className={`mt-2 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            {loadingCaption}
          </p>
        </div>
      )}

      {!isLoading && (
        <div className="mt-5">
          {displayedPdfs.length > 0 ? (
            <HorizontalShelf isDarkMode={isDarkMode} step={560}>
              {displayedPdfs.map((pdf, index) => (
                <div key={`${pdf.PMID}-${pdf.keyword || ''}-${index}`} className="snap-start">
                  <RecommendedPDF
                    PMID={pdf.PMID}
                    journalName={pdf.JT}
                    title={pdf.TI}
                    authors={pdf.AU}
                    institution={pdf.AD}
                    metrics={{
                      if: pdf.IF,
                      casBlock: pdf.B,
                      jcrQ: pdf.Q
                    }}
                    keyword={pdf.keyword}
                    isDarkMode={isDarkMode}
                    onClick={() => handlePubMedClick(pdf.PMID)}
                    pubDate={pdf.PubDate}
                    compact
                  />
                </div>
              ))}
            </HorizontalShelf>
          ) : (
            <div className={`rounded-[24px] border py-10 text-center ${
              isDarkMode ? 'border-white/8 bg-white/5 text-gray-400' : 'border-slate-200 bg-white/80 text-gray-600'
            }`}>
              {isProcessing ? (
                <p className="text-lg font-semibold">{t('homeTab.processingPapers')}</p>
              ) : selectedKeyword ? (
                <>
                  <p className="mb-2 text-lg font-semibold">{t('homeTab.noNewPapers')} "{selectedKeyword}"</p>
                  <p className="text-sm">{t('homeTab.noNewPublications')}</p>
                </>
              ) : (
                <p className="text-lg font-semibold">{t('homeTab.noPapersToDisplay')}</p>
              )}
            </div>
          )}
        </div>
      )}
    </section>
  );
};

export default KeywordSubscriptions;
